import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import type { Ref } from 'vue';
import type { _links } from '@/interfaces/hotel-list-types/hotel-list-types';
import { updateURL, extractUrlFromFormData } from '@/components/common/services/url';
import { formToParams } from '@/components/common/services/transform';
import store from '@/components/common/store';

export function usePagination(links: Ref<_links>, navigationCallback?: (link?: _links[keyof _links]) => Promise<void>) {
	const pageParams = new URLSearchParams(window.location.search);

	const pageQueryParam = parseInt(pageParams.get('page') ?? '1');
	const page = ref(pageQueryParam);

	const hasNext = computed(() => !!links.value.next);

	const updatePageUrl = (pageNumber?: string, createHistoryEntry = false) => {
		const newParams = { ...formToParams(store.state.searchMask), page: pageNumber ?? page.value.toString() };

		if (createHistoryEntry) {
			const url = extractUrlFromFormData(newParams);
			window.history.pushState(null, '', url);
		} else {
			updateURL(newParams);
		}
	};

	const handlePopState = async () => {
		const currentParams = new URLSearchParams(window.location.search);
		const urlPage = parseInt(currentParams.get('page') ?? '1');

		if (urlPage !== page.value) {
			const previousPage = page.value;
			page.value = urlPage;

			if (urlPage === 1) {
				// Navigate to first page
				await navigationCallback?.();
			} else if (urlPage > previousPage) {
				// Navigate forward
				await navigationCallback?.(links.value.next);
			} else {
				// Navigate backward
				await navigationCallback?.(links.value.prev);
			}
		}
	};

	const navigateForward = async () => {
		if (!links.value.next) {
			return;
		}
		page.value = Math.min(page.value + 1);
		await navigationCallback?.(links.value.next);
		updatePageUrl(undefined, true);
	};

	const navigateBack = async () => {
		if (!links.value.prev) {
			return;
		}
		page.value = Math.max(page.value - 1, 1);
		await navigationCallback?.(links.value.prev);
		updatePageUrl(undefined, true);
	};

	const navigateFirst = async () => {
		page.value = 1;
		await navigationCallback?.();
		updatePageUrl(undefined, true);
	};

	onMounted(() => {
		window.addEventListener('popstate', handlePopState);
	});

	onBeforeUnmount(() => {
		window.removeEventListener('popstate', handlePopState);
	});

	return {
		page,
		navigateForward,
		navigateBack,
		navigateFirst,
		hasNext,
		updatePageUrl,
	};
}
